import type { DsdEditorValueOccurrence } from "@/apis/dsd-editor/route";
import { Switch } from "@/toggle-ui-kit/control/switch";
import {
  Tooltip,
  type TooltipAlign,
  type TooltipSide,
} from "@/toggle-ui-kit/tooltip/tooltip";
import { Check } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { formatDsdAxisLabel } from "@/utils/dsd-editor/cell-label-paths";

/**
 * 빨간 경고 아이콘 위에 검은 툴팁을 자동 노출.
 * openKey(메인 셀)가 바뀔 때마다 다시 뜨고, 바깥을 클릭하거나 아이콘을 담은 목록이
 * 스크롤되면 사라진다 (portal이라 스크롤해도 제자리에 남아 고정 헤더 위로 겹친다).
 */
export const AutoDismissWarningTooltip = ({
  label,
  openKey,
  side = "top",
  align,
  children,
}: {
  label: string;
  /** 메인 셀 id 등 — 바뀌면 툴팁 재노출 */
  openKey: string | null;
  side?: TooltipSide;
  /** arrow 정렬 — start=왼쪽, end=오른쪽 */
  align?: TooltipAlign;
  children: React.ReactElement;
}) => {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLSpanElement>(null);

  // 메인 셀이 바뀔 때마다 자동 노출
  useEffect(() => {
    if (openKey) setOpen(true);
  }, [openKey]);

  // 바깥 클릭 시 닫기 — 방금 그 클릭에 바로 닫히지 않도록 다음 틱부터 감지
  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    // 아이콘을 담은 스크롤 컨테이너(Element)일 때만 닫는다. 본문 등 형제 컨테이너 스크롤과
    // 페이지 스크롤(target이 document — Element가 아님)은 무시: 툴팁이 아이콘을 따라가 겹치지 않는다
    const closeOnAncestorScroll = (event: Event) => {
      if (
        event.target instanceof Element &&
        event.target.contains(anchorRef.current)
      )
        close();
    };
    const timer = setTimeout(
      () => document.addEventListener("mousedown", close),
      0,
    );
    document.addEventListener("scroll", closeOnAncestorScroll, true);
    return () => {
      clearTimeout(timer);
      document.removeEventListener("mousedown", close);
      document.removeEventListener("scroll", closeOnAncestorScroll, true);
    };
  }, [open]);

  return (
    <Tooltip
      label={label}
      side={side}
      align={align}
      open={open}
      contentClassName="bg-static-black duration-150 animate-in fade-in-0 zoom-in-95 slide-in-from-bottom-1"
      arrowClassName="fill-static-black"
    >
      <span ref={anchorRef} className="inline-flex shrink-0">
        {children}
      </span>
    </Tooltip>
  );
};

/** 셀 축 라벨: "행라벨 | 열라벨" — 경로에서 최하위(마지막)만 쓴다 (예: "계약손실 충당부채 | 합계") */
export const getOccurrenceAxisLabel = (occurrence: DsdEditorValueOccurrence) =>
  formatDsdAxisLabel(
    occurrence.rowLabelPath?.at(-1) || occurrence.rowLabel || "",
    occurrence.columnLabelPath?.at(-1) || "",
  );

/** '검토 항목만 보기' 토글 — 매칭 탭·기업명 탭 공용 */
export const ReviewOnlyToggle = ({
  active,
  onChange,
}: {
  active: boolean;
  onChange: (value: boolean) => void;
}) => (
  <div className="flex shrink-0 items-center gap-2">
    <span className="caption-1-medium text-label-alternative">
      검토 항목만 보기
    </span>
    <Switch
      size={24}
      active={active}
      onChange={onChange}
      className={active ? "bg-orange-500" : undefined}
      icon={
        active ? (
          <Check className="size-full text-orange-600" strokeWidth={3.5} />
        ) : undefined
      }
    />
  </div>
);

export const EmptyPanelMessage = ({
  title,
  description,
}: {
  title: string;
  description?: string;
}) => (
  <div className="flex flex-1 flex-col items-center justify-center gap-1 px-5 text-center">
    <p className="body-1-normal-medium text-label-normal">{title}</p>
    {description && (
      <p className="caption-1-regular text-label-alternative">{description}</p>
    )}
  </div>
);
